import { ReactNode } from 'react';

import { Permissions, RequestPermissions } from '@/app/types';
import CompassPermissionChip from '@/app/pages/LobbyPage/CompassPermissionChip';
import DevicePermissionChip from '@/app/pages/LobbyPage/DevicePermissionChip';
import LobbyDeviceWarnings from '@/app/pages/LobbyPage/LobbyDeviceWarnings';
import LocationPermissionChip from '@/app/pages/LobbyPage/LocationPermissionChip';
import useCompassPermission from '@/app/hooks/useCompassPermission';
import useLocationPermission, { LocationPermissionState } from '@/app/hooks/useLocationPermission';
import { cn } from '@/lib/utils';

interface LobbyJoinFlowProps {
  compass: ReturnType<typeof useCompassPermission>;
  denied: Permissions;
  isClient: boolean;
  joinButton: ReactNode;
  location: ReturnType<typeof useLocationPermission>;
  permissions: Permissions;
  requestPermissions: RequestPermissions;
}

const isTracked = (isClient: boolean, state: string): boolean => isClient && state !== 'unsupported';

const isReadyToJoin = (
  permissions: Permissions, tracksLocation: boolean, locationState: LocationPermissionState,
) => permissions.video && permissions.audio && (!tracksLocation || locationState === 'granted');

export default function LobbyJoinFlow({
  compass, denied, isClient, joinButton, location, permissions, requestPermissions,
}: LobbyJoinFlowProps) {
  const tracksLocation = isTracked(isClient, location.state);
  const tracksHeading = isTracked(isClient, compass.state);
  const isReady = isReadyToJoin(permissions, tracksLocation, location.state);

  // Conditions, then readiness, then the action. The chips sit against the
  // button so the customer confirms what they are about to allow in one glance
  // instead of pairing a checklist with a button a section away.
  return (
    <div
      className={cn(
        'md:bottom-0 md:z-10 md:sticky flex flex-col gap-3',
        'md:bg-white/95 md:dark:bg-gray-900/95 md:backdrop-blur-sm md:pt-4 md:pb-6',
      )}>
      <h2 className="font-bold text-gray-900 dark:text-gray-100 paragraph-2">
        {isReady ? 'Ready to join' : 'Almost ready'}
      </h2>
      <ul className="flex flex-wrap gap-2">
        <DevicePermissionChip
          hasPermission={permissions.video}
          permission="video"
          requestPermissions={requestPermissions} />
        <DevicePermissionChip
          hasPermission={permissions.audio}
          permission="audio"
          requestPermissions={requestPermissions} />
        {tracksLocation && <LocationPermissionChip {...location} />}
        {tracksHeading && <CompassPermissionChip {...compass} />}
      </ul>
      <LobbyDeviceWarnings denied={denied} />
      {/* On a phone the same button rides along the bottom of the screen. */}
      <div className="hidden md:block">{joinButton}</div>
    </div>
  );
}
